/**
 * Conversa do cliente com a equipe Letscom (API: /minha-conversa/mensagens e /minha-conversa/participantes) e notificações (/notificacoes).
 */

type IAutorTipoMensagem = "interno" | "cliente" | "subordinado";

type IAnexoMensagem = {
  id: number;
  nome_original: string;
  mime_type: string | null;
  tamanho_bytes: number;
  created_at: string;
};

type IMensagemConversa = {
  id: number;
  cliente_id: number;
  conteudo: string;
  autor: { id: number | null; nome: string; tipo: IAutorTipoMensagem };
  /** Mensagem escrita pelo usuário logado (alinha à direita). */
  eh_minha: boolean;
  editada: boolean;
  editada_em: string | null;
  created_at: string;
  updated_at: string;
  anexos: IAnexoMensagem[];
};

type IPaginacaoApi = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type IRespostaListaMensagens = {
  code: number;
  status: string;
  message: string;
  data: IMensagemConversa[];
  pagination: IPaginacaoApi;
};

type IResponderConversaPayload = {
  conteudo: string;
  anexos: File[];
};

type IPapelParticipante = "dono" | "convidado";
/** `inativo`: já participou, mas o dono desativou o acesso (o histórico fica; pode ser reativado). */
type IStatusConvite = "pendente" | "aceito" | "recusado" | "inativo";
type ITipoParticipante = "interno" | "cliente" | "subordinado";

type IParticipanteConversa = {
  id: number;
  cliente_id: number;
  papel: IPapelParticipante;
  status: IStatusConvite;
  lado: "interno" | "cliente";
  tipo: ITipoParticipante;
  pessoa: { id: number | null; nome: string; email: string | null };
  /** O participante é o usuário logado. */
  eh_eu: boolean;
  convidado_em: string;
  respondido_em: string | null;
  /** Quando o acesso foi inativado (null se ativo). */
  inativado_em: string | null;
};

/**
 * Estado da conversa para o usuário logado. Só quem tem acesso (dono ou convite aceito)
 * recebe a lista de participantes; quem tem convite pendente vê só a própria participação.
 */
type IEstadoConversa = {
  iniciada: boolean;
  tem_acesso: boolean;
  /** Só os dois donos (quem iniciou e com quem foi iniciada) convidam, inativam e reativam. */
  pode_convidar: boolean;
  minha_participacao: { id: number; papel: IPapelParticipante; status: IStatusConvite } | null;
  participantes: IParticipanteConversa[];
};

/** Retorno de inativar/reativar: mensagem da API + participante atualizado. */
type IRespostaAcaoParticipante = {
  message: string;
  data: IParticipanteConversa;
};

/** Pessoa da empresa que o dono pode convidar (principal ou subordinado). */
type ICandidatoConvite = {
  user_id: number | null;
  subordinado_id: number | null;
  nome: string | null;
  email: string;
  tipo: ITipoParticipante;
};

type IConvidarParticipantePayload = { user_id: number } | { subordinado_id: number };

/** Tipos conhecidos (`mensagem_conversa` traz `conversa_mensagem_id`; `convite_conversa` traz `convite`). */
type ITipoNotificacao = "mensagem_conversa" | "convite_conversa";

type INotificacao = {
  id: number;
  tipo: ITipoNotificacao | (string & {});
  titulo: string;
  mensagem: string;
  /** Rota a abrir ao clicar (para o cliente: /minha-conversa). */
  link: string | null;
  conversa_mensagem_id: number | null;
  /** Preenchido nas notificações de convite (tipo `convite_conversa`). */
  convite: { id: number; status: IStatusConvite } | null;
  lida: boolean;
  lida_em: string | null;
  created_at: string;
};

type IRespostaListaNotificacoes = {
  code: number;
  status: string;
  message: string;
  data: INotificacao[];
  pagination: IPaginacaoApi;
  nao_lidas: number;
};

/** Mesmas regras da API (validadas também no servidor). */
const TIPO_NOTIFICACAO_CONVITE = "convite_conversa";
const TIPO_NOTIFICACAO_MENSAGEM = "mensagem_conversa";

const MAXIMO_ANEXOS_CONVERSA = 5;
const TAMANHO_MAXIMO_ANEXO_BYTES = 10 * 1024 * 1024;
const TAMANHO_MAXIMO_MENSAGEM = 5000;
const EXTENSOES_ANEXO_PERMITIDAS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "txt",
  "zip",
] as const;
const EXTENSOES_IMAGEM = ["jpg", "jpeg", "png", "gif", "webp"];

/** Anexo exibível como imagem (pelo mime type ou, na falta dele, pela extensão). */
function ehImagemAnexo(arquivo: { mimeType?: string | null; nome: string }): boolean {
  if (arquivo.mimeType) return arquivo.mimeType.startsWith("image/");
  const extensao = arquivo.nome.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSOES_IMAGEM.includes(extensao);
}

/**
 * EXPORTS
 */
export type {
  IAnexoMensagem,
  IAutorTipoMensagem,
  ICandidatoConvite,
  IConvidarParticipantePayload,
  IEstadoConversa,
  IMensagemConversa,
  INotificacao,
  IPaginacaoApi,
  IParticipanteConversa,
  IResponderConversaPayload,
  IRespostaAcaoParticipante,
  IRespostaListaMensagens,
  IRespostaListaNotificacoes,
  IStatusConvite,
  ITipoNotificacao,
};
export {
  EXTENSOES_ANEXO_PERMITIDAS,
  ehImagemAnexo,
  MAXIMO_ANEXOS_CONVERSA,
  TAMANHO_MAXIMO_ANEXO_BYTES,
  TAMANHO_MAXIMO_MENSAGEM,
  TIPO_NOTIFICACAO_CONVITE,
  TIPO_NOTIFICACAO_MENSAGEM,
};
